import type { DocumentGroup } from "services/document-group-api";
import type { TocEntry } from "../metadoc.types";

type TocEntryContentFilterParams = {
  selectedDocumentTypes?: string[];
  hasExplicitDocumentTypeSelection?: boolean;
  searchQuery?: string;
  dateRangeStart?: string | null;
  dateRangeEnd?: string | null;
  matchingDateDocumentGroupIds?: Set<number>;
  matchingMedicationDocumentGroupIds?: Set<number>;
  matchingCodeDocumentGroupIds?: Set<number>;
  medicationSearchQuery?: string;
  codeSearchQuery?: string;
};

type TocEntryFilterParams = TocEntryContentFilterParams & {
  pageRangeStart?: number | null;
  pageRangeEnd?: number | null;
};

type TocEntryFilterContext = {
  facilityByDocumentGroupId: Map<number, string>;
};

type VisiblePageNumbersParams = TocEntryContentFilterParams & {
  entries: TocEntry[];
  totalPages: number;
  facilityByDocumentGroupId: Map<number, string>;
  hasEntryVisibilityFilter: boolean;
  pageRangeStart?: number | null;
  pageRangeEnd?: number | null;
};

const buildAllPageNumbers = (totalPages: number): number[] =>
  Array.from({ length: Math.max(totalPages, 0) }, (_, index) => index + 1);

export const createFacilityByDocumentGroupId = (
  documentGroups: DocumentGroup[],
): Map<number, string> => {
  const facilityMap = new Map<number, string>();

  for (const group of documentGroups) {
    if (group.facility) {
      facilityMap.set(group.id, group.facility);
    }
  }

  return facilityMap;
};

export const matchesTocEntryContentFilters = (
  entry: TocEntry,
  {
    selectedDocumentTypes = [],
    hasExplicitDocumentTypeSelection = false,
    searchQuery = "",
    dateRangeStart = null,
    dateRangeEnd = null,
    matchingDateDocumentGroupIds,
    matchingMedicationDocumentGroupIds,
    matchingCodeDocumentGroupIds,
    medicationSearchQuery = "",
    codeSearchQuery = "",
  }: TocEntryContentFilterParams,
  { facilityByDocumentGroupId }: TocEntryFilterContext,
): boolean => {
  const facility = entry.documentGroupId
    ? facilityByDocumentGroupId.get(entry.documentGroupId)
    : undefined;
  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const matchesSearch =
    normalizedSearchQuery.length === 0 ||
    entry.category.toLowerCase().includes(normalizedSearchQuery) ||
    (entry.provider?.toLowerCase().includes(normalizedSearchQuery) ?? false) ||
    (facility?.toLowerCase().includes(normalizedSearchQuery) ?? false);
  const matchesSelectedDocumentTypes =
    (!hasExplicitDocumentTypeSelection && selectedDocumentTypes.length === 0) ||
    selectedDocumentTypes.includes(entry.category);
  const matchesDateRange =
    (dateRangeStart === null && dateRangeEnd === null) ||
    (typeof entry.documentGroupId === "number" &&
      (matchingDateDocumentGroupIds?.has(entry.documentGroupId) ?? false));
  const matchesMedicationSearch =
    medicationSearchQuery.trim().length === 0 ||
    (typeof entry.documentGroupId === "number" &&
      (matchingMedicationDocumentGroupIds?.has(entry.documentGroupId) ??
        false));
  const matchesCodeSearch =
    codeSearchQuery.trim().length === 0 ||
    (typeof entry.documentGroupId === "number" &&
      (matchingCodeDocumentGroupIds?.has(entry.documentGroupId) ?? false));

  return (
    matchesSearch &&
    matchesSelectedDocumentTypes &&
    matchesDateRange &&
    matchesMedicationSearch &&
    matchesCodeSearch
  );
};

export const matchesTocEntryFilters = (
  entry: TocEntry,
  {
    pageRangeStart = null,
    pageRangeEnd = null,
    ...contentFilters
  }: TocEntryFilterParams,
  context: TocEntryFilterContext,
): boolean => {
  const entryEndPage = entry.endPage ?? entry.startPage;
  const matchesPageRange =
    (pageRangeStart === null || entryEndPage >= pageRangeStart) &&
    (pageRangeEnd === null || entry.startPage <= pageRangeEnd);

  return (
    matchesTocEntryContentFilters(entry, contentFilters, context) &&
    matchesPageRange
  );
};

export const getVisiblePageNumbersForTocFilters = ({
  entries,
  totalPages,
  facilityByDocumentGroupId,
  hasEntryVisibilityFilter,
  pageRangeStart = null,
  pageRangeEnd = null,
  ...contentFilters
}: VisiblePageNumbersParams): number[] => {
  const allPageNumbers = buildAllPageNumbers(totalPages);
  const hasPageRangeFilter = pageRangeStart !== null || pageRangeEnd !== null;

  if (!hasEntryVisibilityFilter && !hasPageRangeFilter) {
    return allPageNumbers;
  }

  const visiblePages = hasEntryVisibilityFilter
    ? new Set<number>()
    : new Set(allPageNumbers);

  if (hasEntryVisibilityFilter) {
    for (const entry of entries) {
      if (
        !matchesTocEntryContentFilters(entry, contentFilters, {
          facilityByDocumentGroupId,
        })
      ) {
        continue;
      }

      const entryEndPage = entry.endPage ?? entry.startPage;
      for (let page = entry.startPage; page <= entryEndPage; page += 1) {
        if (page >= 1 && page <= totalPages) {
          visiblePages.add(page);
        }
      }
    }
  }

  if (hasPageRangeFilter) {
    const visibleRangeStart = Math.max(pageRangeStart ?? 1, 1);
    const visibleRangeEnd = Math.min(pageRangeEnd ?? totalPages, totalPages);

    for (const page of Array.from(visiblePages)) {
      if (page < visibleRangeStart || page > visibleRangeEnd) {
        visiblePages.delete(page);
      }
    }
  }

  return Array.from(visiblePages).sort((left, right) => left - right);
};

export const getNearestVisiblePage = (
  targetPage: number,
  visiblePageNumbers: number[],
): number | null => {
  if (visiblePageNumbers.length === 0) {
    return null;
  }

  let nearestPage = visiblePageNumbers[0];
  let nearestDistance = Math.abs(nearestPage - targetPage);

  for (const page of visiblePageNumbers) {
    const distance = Math.abs(page - targetPage);

    if (distance < nearestDistance) {
      nearestPage = page;
      nearestDistance = distance;
      continue;
    }

    if (distance === nearestDistance && page > nearestPage) {
      nearestPage = page;
    }
  }

  return nearestPage;
};
